import axios from 'axios';

// Configuración de las URLs de la API
const API_URLS = {
    production: 'https://centro-infantil-mijardin.onrender.com/api/matricula',
    development: 'http://localhost:5000/api/matricula'
};

// Determinar la URL base según el entorno
const API_URL = process.env.NODE_ENV === 'production' ? API_URLS.production : API_URLS.development;

// Configuración global de axios
axios.defaults.withCredentials = true;

// Configuración de cache en localStorage
const CACHE_KEYS = {
  matriculas: 'matriculas_cache',
  padres: 'padres_cache'
};

const CACHE_TTL = {
  matriculas: 15 * 60 * 1000, // 15 minutos (aumentado para mejor rendimiento)
  padres: 20 * 60 * 1000 // 20 minutos
};

// Función para obtener datos del cache de localStorage
const getCacheFromStorage = (cacheKey) => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS[cacheKey]);
    if (!cached) return null;
    return JSON.parse(cached);
  } catch (error) {
    console.error('Error al leer cache de localStorage:', error);
    return null;
  }
};

// Función para guardar datos en el cache de localStorage
const setCacheToStorage = (cacheKey, data) => {
  try {
    const cacheData = {
      data: data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEYS[cacheKey], JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error al guardar cache en localStorage:', error);
  }
};

// Función para verificar si el cache es válido
const isCacheValid = (cacheKey) => {
  const cached = getCacheFromStorage(cacheKey);
  if (!cached || !cached.data || !cached.timestamp) return false;
  return (Date.now() - cached.timestamp) < CACHE_TTL[cacheKey];
};

// Función para limpiar cache
const clearCache = (cacheKey = null) => {
  if (cacheKey) {
    localStorage.removeItem(CACHE_KEYS[cacheKey]);
  } else {
    Object.values(CACHE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }
};

// Función para registrar un padre
export const registrarPadre = async (datosPadre) => {
    try {
        // Obtenemos el token de localStorage
        const token = localStorage.getItem('token');

        // Verificamos si el token existe
        if (!token) {
            throw new Error('No se encontró un token de autenticación.');
        }

        // Realizamos la solicitud POST con los datos y el encabezado de autorización
        const response = await axios.post(`${API_URL}/registrar-padre`, datosPadre, {
            headers: {
                Authorization: `Bearer ${token}`, // Enviar el token en los encabezados
            }
        });

        // Limpiar cache de padres al registrar uno nuevo
        clearCache('padres');

        return response.data; // Regresamos la respuesta de la API
    } catch (error) {
        if (error.response) {
            // Manejar errores específicos de cédula y email duplicados
            if (error.response.data.message.includes('cédula ingresada')) {
                throw new Error('La cédula ingresada ya se encuentra registrada en el sistema. Por favor, verifique el número o contacte al administrador si considera que existe un error.');
            }
            
            if (error.response.data.message.includes('correo electrónico ingresado')) {
                throw new Error('El correo electrónico ingresado ya está siendo utilizado por otra cuenta. Por favor, utilice un correo electrónico diferente o recupere su contraseña si la cuenta le pertenece.');
            }

            // Si es otro tipo de error de la API

            throw new Error(error.response.data.message || 'Error al registrar el padre');
        } else {
            // Si es un error en la solicitud

            throw new Error('Ha ocurrido un error al registrar el usuario. Por favor, inténtelo nuevamente más tarde o contacte al soporte técnico.');
        }
    }
};

// **Nueva función** para verificar si un padre tiene hijos
export const verificarHijos = async (idPadre) => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No se encontró un token de autenticación.');
        }

        // Verificar que el usuario tenga el rol correcto
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) {
            throw new Error('No se encontró información del usuario. Por favor vuelve a iniciar sesión.');
        }
        
        if (user.rol !== 'Padre') {
            throw new Error('Solo los padres pueden verificar información de sus hijos.');
        }
        
        // Verificar que el idPadre coincida con el usuario actual
        if (user.id !== idPadre) {
            throw new Error('No puedes verificar información de otros padres.');
        }

        const response = await axios.get(`${API_URL}/verificar-hijos/${idPadre}`, {
            headers: {
                Authorization: `Bearer ${token}`, // Enviar el token en los encabezados
            },
        });

        return response.data;
    } catch (error) {
        handleApiError(error, 'Error al verificar los hijos');
    }
};

// Función para registrar la matrícula de un niño
export const registrarMatricula = async (datosMatricula) => {
    try {
        // Obtenemos el token de localStorage
        const token = localStorage.getItem('token');

        // Verificamos si el token existe
        if (!token) {
            throw new Error('No se encontró un token de autenticación.');
        }

        const response = await axios.post(`${API_URL}/registrar-matricula`, datosMatricula, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        });

        return response.data; 
    } catch (error) {
        if (error.response) {
            // Manejar error específico de cédula duplicada
            if (error.response.data.message === "La cédula ya está registrada para otro niño.") {
                throw new Error("La cédula ya está registrada para otro niño. Por favor verifique el número ingresado.");
            }
            
            // Si es otro tipo de error de la API

            throw new Error(error.response.data.message || 'Error al registrar la matrícula');
        } else {
            // Si es un error en la solicitud

            throw new Error('Ha ocurrido un error al registrar la matrícula. Por favor, inténtelo nuevamente más tarde.');
        }
    }
};

/**
 * Obtiene todas las matrículas del sistema (OPTIMIZADA CON CACHE MEJORADO)
 * @param {boolean} forceRefresh - Forzar actualización ignorando cache
 * @returns {Promise<Object>} Lista de matrículas
 */
export const obtenerTodasLasMatriculas = async (forceRefresh = false) => {
    try {
        // Verificar cache si no se fuerza la actualización
        if (!forceRefresh) {
            const cached = getCacheFromStorage('matriculas');
            
            // Verificar si el cache es válido y tiene datos
            if (cached && cached.data && isCacheValid('matriculas')) {

                
                const cachedData = cached.data;
                
                // Validación básica de estructura de datos
                if (cachedData && cachedData.matriculas && Array.isArray(cachedData.matriculas)) {
                    // Solo verificar datos críticos para evitar invalidaciones innecesarias
                    const hasStructuralIssues = cachedData.matriculas.some(m => 
                        !m || 
                        typeof m !== 'object' ||
                        !m.nino || 
                        !m.matricula ||
                        !m.nino.id_child ||
                        !m.matricula.id_matricula
                    );
                    
                    if (hasStructuralIssues) {
                        clearCache('matriculas');
                    } else {
                        return cachedData;
                }
            } else {
                    clearCache('matriculas');
                }
            } else {

            }
        }


        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No se encontró un token de autenticación.');
        }

        const response = await axios.get(`${API_URL}/obtener-matriculas`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            timeout: 30000 // 30 segundos de timeout
        });

        // Validar respuesta del servidor antes de guardar en cache
        if (response.data && response.data.matriculas && Array.isArray(response.data.matriculas)) {
            // Guardar en localStorage solo si los datos son válidos
            setCacheToStorage('matriculas', response.data);

        } else {

        }

        return response.data;
    } catch (error) {
        // Limpiar cache en caso de error
        clearCache('matriculas');
        handleApiError(error, 'Error al obtener las matrículas');
    }
};

// Función para obtener todos los padres
export const obtenerTodosPadres = async () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No se encontró un token de autenticación.');
        }

        const response = await axios.get(`${API_URL}/obtener-padres`, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        });

        return response.data;
    } catch (error) {
        handleApiError(error, 'Error al obtener los padres');
    }
};

// Función para obtener un padre por ID
export const obtenerPadrePorId = async (idUsuario) => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No se encontró un token de autenticación.');
        }

        const response = await axios.get(`${API_URL}/obtener-padre/${idUsuario}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        });

        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 404) {
            throw new Error('Padre no encontrado');
        }
        handleApiError(error, 'Error al obtener el padre');
    }
};

// Función para actualizar un padre
export const actualizarUsuarioPerfil = async (idUsuario, datosActualizados) => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No se encontró un token de autenticación.');
        }

        const response = await axios.put(`${API_URL}/actualizar-usuario-perfil/${idUsuario}`, datosActualizados, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        });

        // Verificar si la actualización fue exitosa
        if (response.data.success) {
            return {
                success: true,
                message: response.data.message,
                padre: response.data.padre || response.data.data // Asegurarse de manejar ambas respuestas
            };
        } else {
            // Si hay un error específico de correo duplicado, lanzar un error con código
            if (response.data.error === 'EMAIL_EXISTS') {
                const error = new Error(response.data.message);
                error.code = 'EMAIL_EXISTS';
                throw error;
            }
            throw new Error(response.data.message || 'Error al actualizar el perfil');
        }

    } catch (error) {
        if (error.response) {
            const errorData = error.response.data;
            
            // Manejar diferentes casos de error según el mensaje
            if (error.response.status === 404) {
                throw new Error('No se encontró el padre con el ID especificado');
            }
            
            if (errorData.message === 'El correo electrónico ya está siendo utilizado por otra cuenta') {
                throw new Error('El correo electrónico ya está siendo utilizado por otra cuenta');
            }
            
            if (errorData.message === 'La cédula ya está registrada en el sistema') {
                throw new Error('La cédula ya está registrada en el sistema');
            }

            throw new Error(errorData.message || 'Error al actualizar el padre');
        }
        
        console.error('Error al actualizar padre:', error);
        throw new Error('Error interno del servidor al actualizar el padre');
    }
};

// Función para obtener una matrícula por ID del niño
export const obtenerMatriculaPorId = async (idChild) => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No se encontró un token de autenticación.');
        }

        const response = await axios.get(`${API_URL}/obtener-matricula/${idChild}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        });

        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 404) {
            throw new Error('Matrícula no encontrada');
        }
        handleApiError(error, 'Error al obtener la matrícula');
    }
};

// Función para actualizar una matrícula (OPTIMIZADA)
export const actualizarMatricula = async (idChild, datosActualizados) => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No se encontró el token de autenticación');
        }

        const response = await axios.put(`${API_URL}/actualizar-matricula/${idChild}`, datosActualizados, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        // Limpiar cache de matrículas al actualizar
        clearCache('matriculas');

        return response.data;
    } catch (error) {
        console.error('Error al actualizar matrícula:', error);
        if (error.response) {
            throw new Error(error.response.data.message || 'Error del servidor');
        } else if (error.request) {
            throw new Error('No se pudo conectar con el servidor');
        } else {
            throw new Error('Error al procesar la solicitud');
        }
    }
};

// Función para limpiar cache manualmente (útil para refrescar datos)
export const limpiarCacheMatriculas = () => {
    clearCache('matriculas');
};

// Función para limpiar completamente todo el cache y forzar actualización
export const limpiarTodoElCache = () => {
    clearCache(); // Limpia todo el cache
    console.log('Cache completamente limpiado');
};

// Función para verificar y limpiar datos corruptos del cache
export const verificarYLimpiarCacheCorrupto = () => {
    const estadoCache = obtenerEstadoCache();
    
    if (estadoCache.matriculas.tieneData) {
        const cached = getCacheFromStorage('matriculas');
        const cachedData = cached ? cached.data : null;
        
        if (cachedData && cachedData.matriculas && Array.isArray(cachedData.matriculas)) {
            // Solo considerar datos como corruptos si hay problemas graves de estructura
            const hasInvalidData = cachedData.matriculas.some(m => 
                !m || // Registro completamente nulo
                typeof m !== 'object' || // No es un objeto
                !m.nino || // No tiene objeto nino
                !m.matricula || // No tiene objeto matricula
                typeof m.nino !== 'object' || // nino no es objeto
                typeof m.matricula !== 'object' || // matricula no es objeto
                !m.nino.id_child || // No tiene ID del niño (campo crítico)
                !m.matricula.id_matricula // No tiene ID de matrícula (campo crítico)
            );
            
            if (hasInvalidData) {
                console.log('Datos estructuralmente corruptos detectados en cache, limpiando...');
                clearCache('matriculas');
                return true; // Indica que se encontraron datos corruptos
            }
        } else if (cachedData && !cachedData.matriculas) {
            // Si el cache existe pero no tiene la estructura esperada
            console.log('Estructura de cache inválida, limpiando...');
            clearCache('matriculas');
            return true;
        }
    }
    
    return false; // No se encontraron datos corruptos
};

// Función para obtener estado del cache (útil para debugging)
export const obtenerEstadoCache = () => {
    const matriculasCache = getCacheFromStorage('matriculas');
    const padresCache = getCacheFromStorage('padres');
    
    return {
        matriculas: {
            tieneData: !!(matriculasCache && matriculasCache.data),
            timestamp: matriculasCache ? matriculasCache.timestamp : null,
            esValido: isCacheValid('matriculas')
        },
        padres: {
            tieneData: !!(padresCache && padresCache.data),
            timestamp: padresCache ? padresCache.timestamp : null,
            esValido: isCacheValid('padres')
        }
    };
};

const handleApiError = (error, defaultMessage) => {
    if (error.response) {
        console.error('Error de API:', error.response.data);
        console.error('Código de estado:', error.response.status);
        
        // Manejar errores específicos de autorización
        if (error.response.status === 403) {
            const errorData = error.response.data;
            if (errorData.message === 'Unauthorized role') {
                throw new Error('No tienes permisos para realizar esta acción. Verifica que tengas el rol correcto.');
            }
        }
        
        if (error.response.status === 401) {
            throw new Error('Tu sesión ha expirado. Por favor vuelve a iniciar sesión.');
        }
        
        // Si hay un mensaje específico del servidor, usarlo
        if (error.response.data && error.response.data.message) {
            throw new Error(error.response.data.message);
        }
    } else {
        console.error(defaultMessage, error.message);
    }
    throw new Error(defaultMessage);
}